import { useState } from 'react';
import { authAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import awtfLogo from '../assets/awtf_logo.png';

/** Shown instead of the app whenever user.must_change_password is true (see ProtectedRoute in App.jsx) — e.g. an account created by Account Setup with a temporary password. Blocks navigation until a new password is set. */
export default function ForcePasswordChange() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const inputCls = 'w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20';

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setBusy(true);
    try {
      await authAPI.changePassword({ old_password: oldPassword, new_password: newPassword });
      const current = useAuthStore.getState().user;
      const updated = { ...current, must_change_password: false };
      localStorage.setItem('user', JSON.stringify(updated));
      useAuthStore.setState({ user: updated });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set new password. Check your temporary password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(145deg,#1b2857_0%,#4a1a06_45%,#f07415_100%)] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-[24px] bg-white/95 p-3 shadow-[0_16px_40px_rgba(0,0,0,0.3)] ring-1 ring-white/30">
            <img src={awtfLogo} alt="AWTF" className="h-full w-full object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-white">Set a New Password</h1>
          <p className="mt-2 text-sm text-orange-100/80">For your security, please set your own password before continuing.</p>
        </div>

        <div className="overflow-hidden rounded-[32px] border border-white/12 bg-white p-6 shadow-[0_40px_80px_rgba(0,0,0,0.4)]">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Temporary Password</label>
              <input type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} className={inputCls} placeholder="The password you were sent" required />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">New Password</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputCls} placeholder="Min. 6 characters" required />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Confirm New Password</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputCls} placeholder="Repeat new password" required />
            </div>
            <button type="submit" disabled={busy}
              className="w-full rounded-2xl bg-[linear-gradient(135deg,#f07415,#1b2857)] py-3 text-sm font-semibold text-white shadow-[0_12px_25px_rgba(240,116,21,0.40)] transition-transform hover:-translate-y-0.5 disabled:opacity-60">
              {busy ? 'Saving…' : 'Set Password & Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
